import {
  ApiDomainHoster,
  ApiDomainResponse,
  ApiDomainStatus,
} from "@incendium/api";
import { Delete, Done, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CopyClipboardBtn from "Components/CopyClipboardBtn/CopyClipboardBtn";
import GlassCard from "Components/GlassCard/GlassCard";
import { friendlyDateTime } from "Helpers/dates";
import { useMemo } from "react";
import { SubdomainStatusLabel } from "features/subdomains";
import {
  buildFqdn,
  getCloudflareCnameValue,
  getRootDomain,
} from "features/subdomains/utils";
import useProjectTimezone from "Hooks/useProjectTimezone";

interface ISubdomainCardProps {
  subdomain: ApiDomainResponse;
  onEdit: (s: ApiDomainResponse) => void;
  onDelete: (s: ApiDomainResponse) => void;
  onMigrate: (s: ApiDomainResponse) => void;
}

function SubdomainCard({
  subdomain,
  onEdit,
  onDelete,
  onMigrate,
}: ISubdomainCardProps) {
  const timezone = useProjectTimezone();
  const domain = useMemo(() => {
    return `.${getRootDomain(subdomain?.domain || "")}`;
  }, [subdomain.domain]);

  const isCloudflare =
    subdomain.hoster === ApiDomainHoster.DOMAIN_HOSTER_CLOUDFLARE;

  const { cnameName, cnameValue } = useMemo(() => {
    if (isCloudflare && subdomain.subdomain && subdomain.domain) {
      return {
        cnameName: buildFqdn(subdomain.domain, subdomain.subdomain),
        cnameValue: getCloudflareCnameValue(),
      };
    }
    return {
      cnameName: subdomain.cnameName,
      cnameValue: subdomain.cnameValue,
    };
  }, [
    isCloudflare,
    subdomain.subdomain,
    subdomain.domain,
    subdomain.cnameName,
    subdomain.cnameValue,
  ]);

  const handleMigrate = () => {
    onMigrate(subdomain);
  };

  return (
    <GlassCard
      disabled={subdomain.status === ApiDomainStatus.DOMAIN_STATUS_DELETING}
    >
      <CardHeader
        titleTypographyProps={{
          variant: "subtitle1",
        }}
        action={
          <Stack direction={"row"}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => onEdit(subdomain)}
            >
              <Edit />
            </IconButton>
            <IconButton
              size="small"
              color="secondary"
              onClick={() => onDelete(subdomain)}
            >
              <Delete />
            </IconButton>
          </Stack>
        }
        title={
          <span>
            {subdomain.subdomain}
            <Typography color={"secondary"} display={"inline"}>
              {domain}
            </Typography>
          </span>
        }
        subheader={
          <SubdomainStatusLabel
            status={subdomain.status!}
            isCloudflare={isCloudflare}
          />
        }
      />

      <CardContent sx={{ paddingY: 0 }}>
        <Typography variant="body2" noWrap fontWeight={600}>
          {subdomain.name}
        </Typography>
        <Typography variant="body2" noWrap color={"secondary"}>
          <Tooltip arrow title={subdomain.description}>
            <span>{subdomain.description || "-"}</span>
          </Tooltip>
        </Typography>
      </CardContent>

      <CardContent>
        <Divider />
        {subdomain.hoster !== ApiDomainHoster.DOMAIN_HOSTER_CLOUDFLARE && (
          <List dense>
            <Stack
              direction={"row"}
              spacing={1}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="subtitle2">
                Certificate CNAME{" "}
                {subdomain.certificateApproved && (
                  <Done color="success" fontSize="inherit" />
                )}
              </Typography>
              {subdomain.certificateApproved && (
                <Typography variant="body2" color={"success.main"}>
                  Approved{" "}
                  {friendlyDateTime(subdomain.certificateApproved, timezone)}
                </Typography>
              )}
            </Stack>

            <Typography variant="body2" color={"secondary"}>
              Add this CNAME record to validate your domain ownership. This step
              confirms your domain ownership, allowing us to create the
              necessary records and generate the SSL certificate for you.
            </Typography>
            <ListItem disableGutters>
              <CopyClipboardBtn
                isIconButton
                disabled={!subdomain.certificateCname}
                variant="text"
                text={subdomain.certificateCname!}
                size="extraSmall"
                sx={{ textAlign: "left" }}
              />

              <ListItemText
                primary="Certificate CNAME Key"
                secondary={
                  subdomain.certificateCname || "Processing - Check back soon"
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <CopyClipboardBtn
                isIconButton
                disabled={!subdomain.certificateCnameValue}
                variant="text"
                text={subdomain.certificateCnameValue!}
                size="extraSmall"
                sx={{ textAlign: "left" }}
              />

              <ListItemText
                primary="Certificate CNAME Value"
                secondary={
                  subdomain.certificateCnameValue ||
                  "Processing - Check back soon"
                }
              />
            </ListItem>
          </List>
        )}
        <List dense>
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Typography variant="subtitle2">
              Subdomain CNAME{" "}
              {subdomain.domainApproved && (
                <Done color="success" fontSize="inherit" />
              )}
            </Typography>
            {subdomain.domainApproved && (
              <Typography variant="body2" color={"success.main"}>
                Approved {friendlyDateTime(subdomain.domainApproved, timezone)}
              </Typography>
            )}
          </Stack>

          {subdomain.hoster !== ApiDomainHoster.DOMAIN_HOSTER_CLOUDFLARE ? (
            <Typography variant="body2" color={"secondary"}>
              Please add this CNAME record to activate the use of this
              subdomain. You can then use this subdomain to create landing
              pages, split campaigns and smart links.
            </Typography>
          ) : (
            <Typography variant="body2" color={"secondary"}>
              Please add this CNAME record to activate the use of landing pages.
              However, this specific CNAME record cannot be created until the
              domain has been validated with the Certificate CNAME value.
            </Typography>
          )}
          <ListItem disableGutters>
            <CopyClipboardBtn
              isIconButton
              disabled={!cnameName}
              variant="text"
              text={cnameName!}
              size="extraSmall"
              sx={{ textAlign: "left" }}
            />

            <ListItemText
              primary="Domain CNAME Key"
              secondary={cnameName || "Processing - Check back soon"}
            />
          </ListItem>
          <ListItem disableGutters>
            <CopyClipboardBtn
              isIconButton
              disabled={!cnameValue}
              variant="text"
              text={cnameValue!}
              size="extraSmall"
              sx={{ textAlign: "left" }}
            />

            <ListItemText
              primary="Domain CNAME Value"
              secondary={cnameValue || "Processing - Check back soon"}
            />
          </ListItem>
        </List>
      </CardContent>
      {subdomain.hoster !== ApiDomainHoster.DOMAIN_HOSTER_CLOUDFLARE && (
        <Box>
          <Button onClick={() => handleMigrate()}>
            Migrate to new provider
          </Button>
          <Typography mt={1} variant="body2" color={"secondary"}>
            This domain is hosted on a legacy provider. Please migrate to the
            new provider to continue using the platform. When clicking migrate,
            we will automatically create the necessary CNAME records for you.
            You will then need to update the CNAME records in your domain
            provider to point to our servers. Once the migration is complete, we
            shall move your landig pages to the new provider.
          </Typography>
        </Box>
      )}
    </GlassCard>
  );
}

export default SubdomainCard;
